import React from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cx } from './cx';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  tone?: AlertTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Optional button or link shown under the text. */
  action?: React.ReactNode;
  className?: string;
}

const META: Record<AlertTone, { icon: React.ReactNode; box: string; ink: string }> = {
  info: { icon: <Info />, box: 'bg-info/9 border-info/28', ink: 'text-info' },
  success: { icon: <CheckCircle2 />, box: 'bg-success/9 border-success/28', ink: 'text-success' },
  warning: { icon: <AlertTriangle />, box: 'bg-warning/9 border-warning/28', ink: 'text-warning' },
  danger: { icon: <XCircle />, box: 'bg-danger/9 border-danger/28', ink: 'text-danger' },
};

/** Inline notice. Only danger interrupts screen readers; the rest are polite. */
export default function Alert({ tone = 'info', title, children, action, className }: AlertProps) {
  const m = META[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-2.5 rounded-control border px-3.5 py-3 font-ui text-small', m.box, className)}
    >
      <span aria-hidden className={cx('mt-px inline-flex [&>svg]:size-4.5', m.ink)}>
        {m.icon}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        {title ? <p className="m-0 font-semibold text-primary">{title}</p> : null}
        {children ? <div className="text-secondary">{children}</div> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </div>
  );
}
